// Ubah info paket: direct correction of the paket info, no chat needed
// (the pencil on the paket info box). Two groups, like the announcement:
//   Identitas paket      project name (required), paket name, kode paket
//                        SPSE, kode RUP, instansi, LPSE, the LPSE link
//                        (stored only, never opened by the app), lokasi
//   Karakteristik dan    jenis pengadaan, metode, kualifikasi, pagu and HPS
//   jadwal               (typed as digits, shown grouped), batas penawaran
//                        (date and hour), durasi (days), tahun anggaran,
//                        status paket, PIC,
//                        and the KAK summary
// Changing jenis, metode or kualifikasi regenerates the checklist (ticks of
// steps still present stay). Empty fields are saved as empty.
import { useEffect, useId, useState, type FormEvent } from "react";
import type { JenisPengadaan, Kualifikasi, MetodePemilihan, PaketSaveInput, Project } from "../api";
import { formatCount } from "../lib/format";
import { Dialog, Field } from "../ui/components";
import { JENIS_LABEL, KUALIFIKASI_LABEL, METODE_LABEL } from "./stages";

const NAME_MAX = 120;

export interface PaketEditDialogProps {
  open: boolean;
  project: Project;
  busy: boolean;
  error: string | null;
  onSubmit: (input: PaketSaveInput) => void;
  onClose: () => void;
}

const squash = (v: string) => v.replace(/\s+/g, " ").trim();

/** "1250000000" from "Rp 1.250.000.000"; "" when there are no digits. */
export function hpsDigits(v: string): string {
  return v.replace(/[^\d]/g, "").replace(/^0+(?=\d)/, "").slice(0, 15);
}

export function isLink(v: string): boolean {
  return /^https?:\/\/\S+$/i.test(v);
}

interface Form {
  name: string;
  paketName: string;
  kodePaket: string;
  kodeRup: string;
  instansi: string;
  lpse: string;
  link: string;
  lokasi: string;
  jenis: JenisPengadaan | "";
  metode: MetodePemilihan | "";
  kualifikasi: Kualifikasi | "";
  /** Digits only. */
  pagu: string;
  hps: string;
  deadline: string;
  deadlineTime: string;
  durasi: string;
  tahun: string;
  statusPaket: string;
  pic: string;
  kak: string;
}

const digits = (n: number | null) => (n == null ? "" : String(Math.round(n)));

function formOf(p: Project): Form {
  return {
    name: p.name,
    paketName: p.paketName ?? "",
    kodePaket: p.kodePaket ?? "",
    kodeRup: p.kodeRup ?? "",
    instansi: p.instansi ?? "",
    lpse: p.lpse ?? "",
    link: p.sumberUrl ?? "",
    lokasi: p.lokasi ?? "",
    jenis: p.jenisPengadaan ?? "",
    metode: p.metode ?? "",
    kualifikasi: p.kualifikasi ?? "",
    pagu: digits(p.pagu),
    hps: digits(p.nilaiHps),
    deadline: p.deadline ?? "",
    deadlineTime: p.deadlineTime ?? "",
    durasi: p.durasiHari == null ? "" : String(p.durasiHari),
    tahun: p.tahunAnggaran == null ? "" : String(p.tahunAnggaran),
    statusPaket: p.statusPaket ?? "",
    pic: p.pic ?? "",
    kak: p.ringkasanKak ?? "",
  };
}

export function PaketEditDialog({ open, project, busy, error, onSubmit, onClose }: PaketEditDialogProps) {
  const id = useId();
  const formId = `${id}-form`;
  const [form, setForm] = useState<Form>(() => formOf(project));
  const [linkError, setLinkError] = useState<string | null>(null);

  // Every opening starts from the stored values.
  useEffect(() => {
    if (!open) return;
    setForm(formOf(project));
    setLinkError(null);
  }, [open, project]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));
  const cleanName = squash(form.name).slice(0, NAME_MAX);
  const ready = cleanName.length > 0 && !busy;
  const regenerates =
    (form.jenis || null) !== project.jenisPengadaan || (form.metode || null) !== project.metode || (form.kualifikasi || null) !== project.kualifikasi;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    const url = form.link.trim();
    if (url && !isLink(url)) {
      setLinkError("Tautan harus diawali http:// atau https://");
      return;
    }
    setLinkError(null);
    const durasi = Number(form.durasi);
    const tahun = Number(form.tahun);
    onSubmit({
      name: cleanName,
      paketName: squash(form.paketName) || null,
      kodePaket: squash(form.kodePaket) || null,
      kodeRup: squash(form.kodeRup) || null,
      instansi: squash(form.instansi) || null,
      lpse: squash(form.lpse) || null,
      sumberUrl: url || null,
      lokasi: squash(form.lokasi) || null,
      jenisPengadaan: form.jenis || null,
      metode: form.metode || null,
      kualifikasi: form.kualifikasi || null,
      pagu: form.pagu ? Number(form.pagu) : null,
      nilaiHps: form.hps ? Number(form.hps) : null,
      deadline: form.deadline || null,
      deadlineTime: form.deadline && form.deadlineTime ? form.deadlineTime : null,
      durasiHari: form.durasi && durasi >= 1 && durasi <= 3650 ? Math.round(durasi) : null,
      tahunAnggaran: form.tahun && tahun >= 2000 && tahun <= 2100 ? tahun : null,
      statusPaket: squash(form.statusPaket) || null,
      pic: squash(form.pic) || null,
      ringkasanKak: form.kak.trim() || null,
    });
  };

  const text = (key: keyof Form, label: string, max: number, extra: { placeholder?: string; autoComplete?: string; hint?: string } = {}) => (
    <Field id={`${id}-${key}`} label={label} hint={extra.hint}>
      <input
        id={`${id}-${key}`}
        autoComplete={extra.autoComplete ?? "off"}
        placeholder={extra.placeholder}
        value={form[key]}
        maxLength={max}
        readOnly={busy}
        aria-describedby={`${id}-${key}-hint`}
        onChange={(e) => set(key, e.currentTarget.value as never)}
      />
    </Field>
  );

  const money = (key: "pagu" | "hps", label: string) => (
    <Field id={`${id}-${key}`} label={label}>
      <input
        id={`${id}-${key}`}
        inputMode="numeric"
        autoComplete="off"
        placeholder="mis. 1.250.000.000"
        value={form[key] ? formatCount(Number(form[key])) : ""}
        readOnly={busy}
        aria-describedby={`${id}-${key}-hint`}
        onChange={(e) => set(key, hpsDigits(e.currentTarget.value))}
      />
    </Field>
  );

  return (
    <Dialog
      open={open}
      title="Ubah info paket"
      busy={busy}
      onClose={onClose}
      footer={
        <button type="submit" form={formId} className="btn btn-primary" disabled={!ready} aria-busy={busy || undefined}>
          Simpan
        </button>
      }
    >
      <form id={formId} className="pj-form pk-form" onSubmit={submit} noValidate>
        <h3 className="pk-form-group">Identitas paket</h3>
        <Field id={`${id}-name`} label="Nama project" required error={error}>
          <input id={`${id}-name`} data-autofocus="" autoComplete="off" value={form.name} maxLength={NAME_MAX} readOnly={busy} aria-describedby={`${id}-name-hint`} onChange={(e) => set("name", e.currentTarget.value)} />
        </Field>
        {text("paketName", "Nama paket", 300, { placeholder: "Sesuai pengumuman di LPSE" })}
        <div className="pk-form-row">
          {text("kodePaket", "Kode paket SPSE", 100)}
          {text("kodeRup", "Kode RUP", 100)}
        </div>
        {text("instansi", "Instansi", 200, { autoComplete: "organization" })}
        <div className="pk-form-row">
          {text("lpse", "LPSE", 200, { placeholder: "mis. LPSE Kabupaten Bandung" })}
          {text("lokasi", "Lokasi pekerjaan", 200, { placeholder: "Kabupaten/kota" })}
        </div>
        <Field id={`${id}-link`} label="Tautan paket di LPSE" error={linkError} hint="Disimpan sebagai catatan; tidak dibuka oleh aplikasi.">
          <input id={`${id}-link`} type="url" inputMode="url" autoComplete="off" placeholder="https://lpse..." value={form.link} maxLength={500} readOnly={busy} aria-describedby={`${id}-link-hint`} aria-invalid={linkError ? true : undefined} onChange={(e) => set("link", e.currentTarget.value)} />
        </Field>

        <h3 className="pk-form-group">Karakteristik dan jadwal</h3>
        <div className="pk-form-row">
          <Field id={`${id}-jenis`} label="Jenis pengadaan">
            <select id={`${id}-jenis`} value={form.jenis} disabled={busy} aria-describedby={`${id}-jenis-hint`} onChange={(e) => set("jenis", e.currentTarget.value as Form["jenis"])}>
              <option value="">Belum dipilih</option>
              {(Object.keys(JENIS_LABEL) as JenisPengadaan[]).map((k) => (
                <option key={k} value={k}>
                  {JENIS_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
          <Field id={`${id}-metode`} label="Metode pemilihan">
            <select id={`${id}-metode`} value={form.metode} disabled={busy} aria-describedby={`${id}-metode-hint`} onChange={(e) => set("metode", e.currentTarget.value as Form["metode"])}>
              <option value="">Belum dipilih</option>
              {(Object.keys(METODE_LABEL) as MetodePemilihan[]).map((k) => (
                <option key={k} value={k}>
                  {METODE_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field
          id={`${id}-kualifikasi`}
          label="Kualifikasi"
          hint={regenerates ? "Checklist akan disusun ulang sesuai jenis, metode, dan kualifikasi. Centang pada langkah yang tetap ada tidak hilang." : undefined}
        >
          <select id={`${id}-kualifikasi`} value={form.kualifikasi} disabled={busy} aria-describedby={`${id}-kualifikasi-hint`} onChange={(e) => set("kualifikasi", e.currentTarget.value as Form["kualifikasi"])}>
            <option value="">Belum dipilih</option>
            {(Object.keys(KUALIFIKASI_LABEL) as Kualifikasi[]).map((k) => (
              <option key={k} value={k}>
                {KUALIFIKASI_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        <div className="pk-form-row">
          {money("pagu", "Pagu (Rp)")}
          {money("hps", "Nilai HPS (Rp)")}
        </div>
        <div className="pk-form-row">
          <Field id={`${id}-deadline`} label="Batas penawaran">
            <input id={`${id}-deadline`} type="date" value={form.deadline} readOnly={busy} aria-describedby={`${id}-deadline-hint`} onChange={(e) => set("deadline", e.currentTarget.value)} />
          </Field>
          <Field id={`${id}-deadlineTime`} label="Jam batas penawaran">
            <input id={`${id}-deadlineTime`} type="time" value={form.deadlineTime} readOnly={busy} aria-describedby={`${id}-deadlineTime-hint`} onChange={(e) => set("deadlineTime", e.currentTarget.value)} />
          </Field>
        </div>
        <div className="pk-form-row">
          <Field id={`${id}-durasi`} label="Durasi pelaksanaan (hari)">
            <input id={`${id}-durasi`} inputMode="numeric" autoComplete="off" placeholder="mis. 90" value={form.durasi} readOnly={busy} aria-describedby={`${id}-durasi-hint`} onChange={(e) => set("durasi", e.currentTarget.value.replace(/[^\d]/g, "").slice(0, 4))} />
          </Field>
          <Field id={`${id}-tahun`} label="Tahun anggaran">
            <input id={`${id}-tahun`} inputMode="numeric" autoComplete="off" placeholder="mis. 2026" value={form.tahun} readOnly={busy} onChange={(e) => set("tahun", e.currentTarget.value.replace(/[^\d]/g, "").slice(0, 4))} />
          </Field>
        </div>
        {text("statusPaket", "Status paket", 100, { placeholder: "mis. Pengumuman, Evaluasi" })}
        {text("pic", "Penanggung jawab penawaran (PIC)", 120)}
        <Field id={`${id}-kak`} label="Ringkasan KAK" hint="Lingkup pekerjaan, keluaran, dan hal penting dari KAK.">
          <textarea id={`${id}-kak`} rows={3} maxLength={2000} value={form.kak} readOnly={busy} aria-describedby={`${id}-kak-hint`} onChange={(e) => set("kak", e.currentTarget.value)} />
        </Field>
      </form>
    </Dialog>
  );
}
